import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";

export default function Navbar({ lang, setLang, t, menuItems, activeSection, scrollToSection }) {
  const [isOpen, setIsOpen] = useState(false);
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef(null);

  // Close menus on route change
  useEffect(() => {
    setIsOpen(false);
    setSolutionsOpen(false);
  }, [location]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setSolutionsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleNavClick = (sectionId) => {
    setIsOpen(false);
    setSolutionsOpen(false);
    if (location.pathname === "/") {
      scrollToSection(sectionId);
    } else {
      window.location.href = `/#${sectionId}`;
    }
  };

  // Helper to determine active link style
  const isSectionActive = (sectionId) => {
    if (sectionId === "home") {
      return activeSection === "home" || activeSection === "what-we-do" || activeSection === "gallery";
    }
    if (sectionId === "solutions") {
      return activeSection === "newalbert" || activeSection === "ai-knowledge";
    }
    return activeSection === sectionId;
  };

  const navLinkClass = (sectionId) =>
    `relative py-2 px-3 text-sm font-semibold transition-all duration-200 select-none tracking-wide cursor-pointer ${
      isSectionActive(sectionId)
        ? "text-brand-blue border-b-2 border-brand-blue"
        : "text-zinc-600 hover:text-brand-blue"
    }`;

  const mobileNavLinkClass = (sectionId) =>
    `block w-full text-left py-3 px-4 text-base font-medium border-l-4 transition-colors duration-200 select-none cursor-pointer ${
      isSectionActive(sectionId)
        ? "text-brand-blue bg-zinc-50 border-brand-blue"
        : "text-zinc-700 hover:text-brand-blue hover:bg-zinc-50 border-transparent"
    }`;

  return (
    <header className="sticky top-0 z-50 w-full bg-white border-b border-zinc-200 shadow-sm">
      {/* Top Orange Accent Line */}
      <div className="w-full border-t-[6px] border-brand-orange bg-white h-1.5"></div>

      <div className="max-w-7xl mx-auto px-6 py-2 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 group select-none py-1" aria-label="Albertsen Group Forside">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=300,quality=90,format=auto/assets/25c88f45-a091-4778-ada1-e41c6b1f66ac/albertsen-group-logo-rez.png"
            alt="Albertsen Group Logo"
            width="240"
            height="64"
            loading="eager"
            fetchPriority="high"
            className="h-14 sm:h-16 w-auto object-contain"
          />
          <div className="flex flex-col">
            <span className="text-2xl font-bold tracking-tight leading-none font-display">
              <span className="text-brand-navy group-hover:text-brand-blue transition-colors duration-200">Albertsen</span>{" "}
              <span className="text-brand-orange">Group</span>
            </span>
            <span className="text-[10px] text-zinc-400 uppercase tracking-[0.15em] font-bold mt-1">
              AI-POWERED KNOWLEDGE MANAGEMENT PLATFORMS
            </span>
          </div>
        </Link>

        {/* Desktop Navigation (>= 1200px) */}
        <nav className="hidden min-[1200px]:flex items-center gap-6" aria-label="Hovednavigation">
          <button onClick={() => handleNavClick("home")} className={navLinkClass("home")}>
            {t("nav.home")}
          </button>
          <button onClick={() => handleNavClick("about")} className={navLinkClass("about")}>
            {t("nav.about")}
          </button>

          {/* Solutions Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setSolutionsOpen(!solutionsOpen)}
              onMouseEnter={() => setSolutionsOpen(true)}
              aria-expanded={solutionsOpen}
              aria-haspopup="true"
              className={`flex items-center gap-1.5 py-2 px-3 text-sm font-semibold transition-all duration-200 select-none cursor-pointer tracking-wide ${
                isSectionActive("solutions")
                  ? "text-brand-blue border-b-2 border-brand-blue"
                  : "text-zinc-600 hover:text-brand-blue"
              }`}
            >
              {t("nav.solutions")}
              <i className={`fa-solid fa-chevron-down text-[9px] transition-transform duration-200 ${solutionsOpen ? "rotate-180" : ""}`} aria-hidden="true" />
            </button>

            {solutionsOpen && (
              <div
                onMouseLeave={() => setSolutionsOpen(false)}
                className="absolute left-0 mt-1 w-72 bg-white border border-zinc-200 shadow-xl rounded-none py-2.5 z-50 animate-fade-in"
              >
                <button
                  onClick={() => handleNavClick("newalbert")}
                  className={`block w-full text-left px-5 py-3 text-sm transition-all duration-150 cursor-pointer ${
                    activeSection === "newalbert"
                      ? "bg-zinc-50 text-brand-blue font-bold"
                      : "text-zinc-700 hover:bg-zinc-50 hover:text-brand-blue"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="h-2 w-2 bg-brand-green rounded-full" aria-hidden="true"></span>
                    <div>
                      <div className="font-semibold">{t("nav.newalbert")}</div>
                      <div className="text-[11px] text-zinc-400 font-normal mt-0.5">AI-driven Research Partner</div>
                    </div>
                  </div>
                </button>
                <button
                  onClick={() => handleNavClick("ai-knowledge")}
                  className={`block w-full text-left px-5 py-3 text-sm transition-all duration-150 cursor-pointer ${
                    activeSection === "ai-knowledge"
                      ? "bg-zinc-50 text-brand-blue font-bold"
                      : "text-zinc-700 hover:bg-zinc-50 hover:text-brand-blue"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="h-2 w-2 bg-brand-blue rounded-full" aria-hidden="true"></span>
                    <div>
                      <div className="font-semibold">{t("nav.aiKnowledge")}</div>
                      <div className="text-[11px] text-zinc-400 font-normal mt-0.5">Semantic Document Analysis</div>
                    </div>
                  </div>
                </button>
              </div>
            )}
          </div>

          <button onClick={() => handleNavClick("contact")} className={navLinkClass("contact")}>
            {t("nav.contact")}
          </button>
        </nav>

        {/* Language Selector & Burger Menu Button */}
        <div className="flex items-center gap-4">
          {/* Language Selector */}
          <div className="flex items-center bg-zinc-100 p-1 border border-zinc-200/60 rounded-none select-none">
            <button
              onClick={() => setLang("en")}
              aria-label="Skift til engelsk sprog"
              className={`px-2.5 py-1 text-xs font-semibold uppercase tracking-wider transition-all duration-150 cursor-pointer ${
                lang === "en"
                  ? "bg-white text-brand-blue shadow-sm font-bold"
                  : "text-zinc-500 hover:text-zinc-800"
              }`}
            >
              EN
            </button>
            <button
              onClick={() => setLang("da")}
              aria-label="Skift til dansk sprog"
              className={`px-2.5 py-1 text-xs font-semibold uppercase tracking-wider transition-all duration-150 cursor-pointer ${
                lang === "da"
                  ? "bg-white text-brand-blue shadow-sm font-bold"
                  : "text-zinc-500 hover:text-zinc-800"
              }`}
            >
              DA
            </button>
          </div>

          {/* Burger Menu Button (< 1200px) */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="flex min-[1200px]:hidden items-center justify-center p-2 text-zinc-700 hover:text-brand-blue transition-colors duration-150 cursor-pointer"
            aria-label={isOpen ? "Luk menu" : "Åbn menu"}
            aria-expanded={isOpen}
          >
            <i className={`fa-solid ${isOpen ? "fa-xmark text-2xl" : "fa-bars text-2xl"}`} aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* Mobile Navigation Menu (< 1200px) */}
      {isOpen && (
        <div className="min-[1200px]:hidden border-t border-zinc-200 bg-white animate-slide-down">
          <nav className="py-2" aria-label="Mobilnavigation">
            <button onClick={() => handleNavClick("home")} className={mobileNavLinkClass("home")}>
              {t("nav.home")}
            </button>
            <button onClick={() => handleNavClick("about")} className={mobileNavLinkClass("about")}>
              {t("nav.about")}
            </button>

            {/* Solutions Section in Mobile */}
            <div className="border-l-4 border-transparent">
              <div className="flex items-center justify-between py-3 px-4 text-base font-medium text-zinc-500 uppercase tracking-wider text-xs bg-zinc-50/50">
                <span>{t("nav.solutions")}</span>
              </div>
              <button
                onClick={() => handleNavClick("newalbert")}
                className={`block w-full text-left py-3 px-6 text-sm font-medium transition-colors duration-200 cursor-pointer ${
                  activeSection === "newalbert"
                    ? "text-brand-blue bg-zinc-50 font-semibold"
                    : "text-zinc-700 hover:text-brand-blue hover:bg-zinc-50/30"
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 bg-brand-green rounded-full" aria-hidden="true"></span>
                  {t("nav.newalbert")}
                </div>
              </button>
              <button
                onClick={() => handleNavClick("ai-knowledge")}
                className={`block w-full text-left py-3 px-6 text-sm font-medium transition-colors duration-200 cursor-pointer ${
                  activeSection === "ai-knowledge"
                    ? "text-brand-blue bg-zinc-50 font-semibold"
                    : "text-zinc-700 hover:text-brand-blue hover:bg-zinc-50/30"
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 bg-brand-blue rounded-full" aria-hidden="true"></span>
                  {t("nav.aiKnowledge")}
                </div>
              </button>
            </div>

            <button onClick={() => handleNavClick("contact")} className={mobileNavLinkClass("contact")}>
              {t("nav.contact")}
            </button>
          </nav>
        </div>
      )}
    </header>
  );
}
